import { Navigate, useParams } from 'react-router-dom';
import {
  faBriefcase,
  faChartColumn,
  faCircleQuestion,
  faComment,
  faCube,
  faDesktop,
  faFileLines,
  faFolderOpen,
  faGauge,
  faGear,
  faHandHoldingHeart,
  faHeartPulse,
  faImages,
  faMagnifyingGlass,
  faMicrochip,
  faMicroscope,
  faNewspaper,
  faNoteSticky,
  faPenToSquare,
  faPlug,
  faRightFromBracket,
  faStethoscope,
  faUser,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import Login from '../pages/Login';
import Logout from '../pages/Logout';
import Portfolio from '../pages/Portfolio';
import Dashboard from '../pages/portfolio/Dashboard';
import MonitoringPlant from '../pages/plant/monitoring/Plant';
import MonitoringAlarms from '../pages/plant/monitoring/Alarms';
import MonitoringWeather from '../pages/plant/monitoring/Weather';
import InsightsAvailability from '../pages/plant/insights/Availability';
import InsightsBenchmarking from '../pages/plant/insights/Benchmarking';
import InsightsSoiling from '../pages/plant/insights/Soiling';
import InsightsPerformanceEngineering from '../pages/plant/insights/PerformanceEngineering';
import InsightsPVUSAModel from '../pages/plant/insights/PVUSAModel';
import InsightsPVSystComparison from '../pages/plant/insights/PVSystComparison';
import DiagnosticsDCBlock from '../pages/plant/diagnostics/DCBlock';
import DiagnosticsACBlock from '../pages/plant/diagnostics/ACBlock';
import DiagnosticsBatteries from '../pages/plant/diagnostics/Batteries';
import DiagnosticsSolarTrackers from '../pages/plant/diagnostics/SolarTrackers';
import DiagnosticsDataQuality from '../pages/plant/diagnostics/DataQuality';
import ReportsDataExports from '../pages/plant/reports/DataExports';
import AssetManagement from '../pages/AssetManagement';
import Messaging from '../pages/Messaging';
import AdminAccount from '../pages/admin/Account';
import AdminSettings from '../pages/admin/Settings';
import AdminAppearance from '../pages/admin/Appearance';
import AdminIntegrations from '../pages/admin/Integrations';
import AdminUsers from '../pages/admin/Users';
import AdminUserProfile from '../pages/admin/UserProfile';
import AdminApplication from '../pages/admin/Application';
import HelpDocumentation from '../pages/help/Documentation';
import HelpFaultCodes from '../pages/help/FaultCodes';
import HelpRoles from '../pages/help/Roles';
import HelpSiteTypes from '../pages/help/SiteTypes';
import HelpReleaseNotes from '../pages/help/ReleaseNotes';
import HelpDataCatalog from '../pages/help/DataCatalog';
import HelpSupport from '../pages/help/Support';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import TermsConditions from '../pages/TermsConditions';
import { PlantDataProvider } from '../components/data/PlantData';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import { PERMISSIONS } from '../auth/permissions';
import { NAV_PLACEMENTS } from './navigationConstants';

const protect = (element, permission) => (
  <ProtectedRoute permission={permission}>{element}</ProtectedRoute>
);

const nav = (placements, label, options = {}) => ({
  placements: Array.isArray(placements) ? placements : [placements],
  label,
  visible: true,
  ...options,
});

function PlantRoutePage({ component: Component }) {
  const { plantName } = useParams();
  return <Component plantName={plantName} />;
}

/**
 * The single source of truth for route matching and application navigation.
 * `handle.nav.visible: false` records intentional product visibility without
 * commenting routes or links out of individual components.
 */
export const vivosolRouteConfig = [
  { path: '/', element: <Navigate replace to="/portfolio" /> },
  { path: '/login', element: <Login />, handle: { breadcrumb: false } },
  { path: '/logout', element: <Logout />, handle: { breadcrumb: false } },
  {
    path: '/portfolio',
    handle: {
      breadcrumb: 'Portfolio',
      breadcrumbTo: '/portfolio',
      nav: nav(NAV_PLACEMENTS.HEADER, 'PORTFOLIO', {
        icon: faBriefcase,
        area: 'primary',
        order: 10,
      }),
    },
    children: [
      {
        index: true,
        element: <Portfolio />,
        handle: {
          sidebarPlacement: NAV_PLACEMENTS.PORTFOLIO,
          nav: nav([NAV_PLACEMENTS.PORTFOLIO, NAV_PLACEMENTS.SITE], 'DASHBOARD', {
            icon: faGauge,
            dynamicChildren: 'plants',
            order: 10,
          }),
        },
      },
      {
        path: 'dashboard',
        handle: { breadcrumb: 'Dashboard', breadcrumbTo: '/portfolio', sidebarPlacement: NAV_PLACEMENTS.SITE },
        children: [
          { index: true, element: <Navigate replace to="/portfolio" /> },
          {
            path: ':portfolioName',
            element: <Dashboard />,
            handle: { breadcrumb: { param: 'portfolioName' } },
          },
        ],
      },
    ],
  },
  {
    path: '/:plantName',
    element: <PlantDataProvider />,
    handle: {
      breadcrumb: { param: 'plantName' },
      breadcrumbTo: '/portfolio/dashboard/:plantName',
      sidebarPlacement: NAV_PLACEMENTS.SITE,
    },
    children: [
      {
        path: 'monitoring',
        handle: {
          breadcrumb: 'Monitoring',
          nav: nav(NAV_PLACEMENTS.SITE, 'MONITORING', {
            icon: faHeartPulse,
            order: 20,
          }),
        },
        children: [
          { index: true, element: <Navigate replace to="plant" /> },
          { path: 'plant', element: <PlantRoutePage component={MonitoringPlant} />, handle: { breadcrumb: 'Plant', nav: nav(NAV_PLACEMENTS.SITE, 'Plant', { order: 10 }) } },
          { path: 'alarms', element: <PlantRoutePage component={MonitoringAlarms} />, handle: { breadcrumb: 'Alarms', nav: nav(NAV_PLACEMENTS.SITE, 'Alarms', { order: 20 }) } },
          { path: 'weather', element: <PlantRoutePage component={MonitoringWeather} />, handle: { breadcrumb: 'Weather', nav: nav(NAV_PLACEMENTS.SITE, 'Weather', { order: 30 }) } },
        ],
      },
      {
        path: 'insights',
        handle: {
          breadcrumb: 'Insights',
          nav: nav(NAV_PLACEMENTS.SITE, 'INSIGHTS', {
            icon: faChartColumn,
            order: 30,
          }),
        },
        children: [
          { index: true, element: <Navigate replace to="performance-engineering" /> },
          { path: 'performance-engineering', element: <InsightsPerformanceEngineering />, handle: { breadcrumb: 'Performance Engineering', nav: nav(NAV_PLACEMENTS.SITE, 'Performance Engineering', { order: 10 }) } },
          { path: 'pv-usa-model', element: <InsightsPVUSAModel />, handle: { breadcrumb: 'PV USA Model', nav: nav(NAV_PLACEMENTS.SITE, 'PV USA Model', { order: 20 }) } },
          { path: 'pv-system-comparison', element: <InsightsPVSystComparison />, handle: { breadcrumb: 'PV System Comparison', nav: nav(NAV_PLACEMENTS.SITE, 'PV System Comparison', { order: 30 }) } },
          { path: 'benchmarking', element: <InsightsBenchmarking />, handle: { breadcrumb: 'Benchmarking', nav: nav(NAV_PLACEMENTS.SITE, 'Benchmarking', { order: 40 }) } },
          { path: 'availability', element: <InsightsAvailability />, handle: { breadcrumb: 'Availability', nav: nav(NAV_PLACEMENTS.SITE, 'Availability', { order: 50 }) } },
          { path: 'soiling', element: <InsightsSoiling />, handle: { breadcrumb: 'Soiling', nav: nav(NAV_PLACEMENTS.SITE, 'Soiling', { order: 60 }) } },
          { path: 'forecast', element: <Navigate replace to="../soiling" />, handle: { breadcrumb: 'Soiling' } },
        ],
      },
      {
        path: 'diagnostics',
        handle: {
          breadcrumb: 'Diagnostics',
          nav: nav(NAV_PLACEMENTS.SITE, 'DIAGNOSTICS', {
            icon: faMicrochip,
            order: 40,
          }),
        },
        children: [
          { index: true, element: <Navigate replace to="dc-block" /> },
          { path: 'dc-block', element: <DiagnosticsDCBlock />, handle: { breadcrumb: 'DC Block', nav: nav(NAV_PLACEMENTS.SITE, 'DC Block', { order: 10 }) } },
          { path: 'ac-block', element: <DiagnosticsACBlock />, handle: { breadcrumb: 'AC Block', nav: nav(NAV_PLACEMENTS.SITE, 'AC Block', { order: 20 }) } },
          { path: 'batteries', element: <DiagnosticsBatteries />, handle: { breadcrumb: 'Batteries', nav: nav(NAV_PLACEMENTS.SITE, 'Batteries', { order: 30, metricFlag: 'battery_racks' }) } },
          { path: 'solar-trackers', element: <DiagnosticsSolarTrackers />, handle: { breadcrumb: 'Solar Trackers', nav: nav(NAV_PLACEMENTS.SITE, 'Solar Trackers', { order: 40 }) } },
          { path: 'data-quality', element: <DiagnosticsDataQuality />, handle: { breadcrumb: 'Data Quality', nav: nav(NAV_PLACEMENTS.SITE, 'Data Quality', { order: 50 }) } },
        ],
      },
      {
        path: 'reports',
        handle: {
          breadcrumb: 'Reports',
          nav: nav(NAV_PLACEMENTS.SITE, 'REPORTS', {
            icon: faFileLines,
            order: 50,
          }),
        },
        children: [
          { index: true, element: <Navigate replace to="data-exports" /> },
          { path: 'data-exports', element: <ReportsDataExports />, handle: { breadcrumb: 'Data Exports', nav: nav(NAV_PLACEMENTS.SITE, 'Data Exports', { order: 10 }) } },
        ],
      },
    ],
  },
  {
    path: '/asset-management',
    element: protect(<AssetManagement />, PERMISSIONS.VIEW_ADMIN),
    handle: {
      breadcrumb: 'Asset Management',
      permission: PERMISSIONS.VIEW_ADMIN,
      nav: nav(NAV_PLACEMENTS.HEADER, 'ASSET MANAGEMENT', {
        visible: false,
        permission: PERMISSIONS.MANAGE_ADMIN,
        area: 'primary',
        order: 20,
      }),
    },
  },
  {
    path: '/messaging',
    element: protect(<Messaging />, PERMISSIONS.VIEW_MESSAGING),
    handle: {
      breadcrumb: 'Messaging',
      permission: PERMISSIONS.VIEW_MESSAGING,
      nav: nav(NAV_PLACEMENTS.HEADER, 'MESSAGING', {
        visible: false,
        permission: PERMISSIONS.VIEW_MESSAGING,
        icon: faComment,
        area: 'quick',
        order: 10,
      }),
      sidebarActions: [
        { label: 'New Chat', path: '/messaging', icon: faPenToSquare },
        { label: 'Search Chats', path: '/messaging', icon: faMagnifyingGlass },
        { label: 'Images', path: '/messaging', icon: faImages },
        { label: 'Apps', path: '/messaging', icon: faPlug },
        { label: 'Deep Research', path: '/messaging', icon: faMicroscope },
        { label: 'Health', path: '/messaging', icon: faStethoscope },
      ],
      sidebarActionsPlacement: NAV_PLACEMENTS.MESSAGING,
      sidebarPlacement: NAV_PLACEMENTS.MESSAGING,
    },
  },
  {
    path: '/admin',
    handle: {
      breadcrumb: 'Admin',
      breadcrumbTo: '/admin/account',
      nav: nav(NAV_PLACEMENTS.HEADER, 'ADMIN', {
        permission: PERMISSIONS.VIEW_ADMIN,
        icon: faUser,
        area: 'quick',
        order: 30,
      }),
      sidebarActions: [
        { label: 'LOGOUT', path: '/logout', icon: faRightFromBracket, order: 100 },
      ],
      sidebarActionsPlacement: NAV_PLACEMENTS.ADMIN,
      sidebarPlacement: NAV_PLACEMENTS.ADMIN,
    },
    children: [
      { index: true, element: <Navigate replace to="account" /> },
      {
        path: 'account',
        element: protect(<AdminAccount />, PERMISSIONS.VIEW_ADMIN),
        handle: { breadcrumb: 'Account', permission: PERMISSIONS.VIEW_ADMIN, nav: nav(NAV_PLACEMENTS.ADMIN, 'MY ACCOUNT', { icon: faUser, order: 10 }) },
      },
      {
        path: 'settings',
        element: protect(<AdminSettings />, PERMISSIONS.VIEW_ADMIN),
        handle: { breadcrumb: 'Settings', permission: PERMISSIONS.VIEW_ADMIN, nav: nav(NAV_PLACEMENTS.ADMIN, 'SETTINGS', { icon: faGear, order: 15 }) },
      },
      {
        path: 'appearance',
        element: protect(<AdminAppearance />, PERMISSIONS.VIEW_ADMIN),
        handle: { breadcrumb: 'Appearance', permission: PERMISSIONS.VIEW_ADMIN, nav: nav(NAV_PLACEMENTS.ADMIN, 'APPEARANCE', { visible: false, icon: faDesktop, order: 30 }) },
      },
      {
        path: 'integrations',
        element: protect(<AdminIntegrations />, PERMISSIONS.MANAGE_ADMIN),
        handle: { breadcrumb: 'Integrations', permission: PERMISSIONS.MANAGE_ADMIN, nav: nav(NAV_PLACEMENTS.ADMIN, 'INTEGRATIONS', { visible: false, permission: PERMISSIONS.MANAGE_ADMIN, icon: faPlug, order: 40 }) },
      },
      {
        path: 'users',
        handle: {
          breadcrumb: 'Users',
          breadcrumbTo: '/admin/users',
          permission: PERMISSIONS.VIEW_ADMIN,
          nav: nav(NAV_PLACEMENTS.ADMIN, 'USERS', { permission: PERMISSIONS.VIEW_ADMIN, appRoleIds: [1, 2], icon: faUsers, order: 20 }),
        },
        children: [
          {
            index: true,
            element: protect(<AdminUsers />, PERMISSIONS.VIEW_ADMIN),
          },
          {
            path: ':userId',
            element: protect(<AdminUserProfile />, PERMISSIONS.VIEW_ADMIN),
            handle: { breadcrumb: 'Profile', permission: PERMISSIONS.VIEW_ADMIN },
          },
        ],
      },
      {
        path: 'application',
        element: protect(<AdminApplication />, PERMISSIONS.VIEW_ADMIN),
        handle: { breadcrumb: 'Application', permission: PERMISSIONS.VIEW_ADMIN, nav: nav(NAV_PLACEMENTS.ADMIN, 'APPLICATION', { permission: PERMISSIONS.VIEW_ADMIN, icon: faCube, order: 30 }) },
      },
    ],
  },
  {
    path: '/help',
    handle: {
      breadcrumb: 'Help',
      breadcrumbTo: '/help/documentation',
      nav: nav(NAV_PLACEMENTS.HEADER, 'HELP', {
        icon: faCircleQuestion,
        area: 'quick',
        order: 20,
      }),
      sidebarPlacement: NAV_PLACEMENTS.HELP,
    },
    children: [
      { index: true, element: <Navigate replace to="documentation" /> },
      {
        path: 'documentation',
        handle: { breadcrumb: 'Documentation', breadcrumbTo: '/help/documentation', nav: nav(NAV_PLACEMENTS.HELP, 'DOCUMENTATION', { icon: faFolderOpen, order: 10 }) },
        children: [
          { index: true, element: <HelpDocumentation /> },
          { path: 'fault-codes', element: <HelpFaultCodes />, handle: { breadcrumb: 'Fault Codes' } },
          { path: 'roles', element: <HelpRoles />, handle: { breadcrumb: 'Roles' } },
          { path: 'site-types', element: <HelpSiteTypes />, handle: { breadcrumb: 'Site Types' } },
        ],
      },
      { path: 'release-notes', element: <HelpReleaseNotes />, handle: { breadcrumb: 'Release Notes', nav: nav(NAV_PLACEMENTS.HELP, 'RELEASE NOTES', { visible: false, icon: faNoteSticky, order: 20 }) } },
      { path: 'data-catalog', element: <HelpDataCatalog />, handle: { breadcrumb: 'Data Catalog', nav: nav(NAV_PLACEMENTS.HELP, 'DATA CATALOG', { visible: false, icon: faNewspaper, order: 30 }) } },
      { path: 'support', element: <HelpSupport />, handle: { breadcrumb: 'Support', nav: nav(NAV_PLACEMENTS.HELP, 'SUPPORT', { visible: false, icon: faHandHoldingHeart, order: 40 }) } },
    ],
  },
  {
    path: '/privacy-policy',
    element: <PrivacyPolicy />,
    handle: { breadcrumb: 'Privacy Policy', sidebarPlacement: NAV_PLACEMENTS.FOOTER, nav: nav(NAV_PLACEMENTS.FOOTER, 'PRIVACY POLICY', { footerLabel: 'Privacy Policy', order: 10 }) },
  },
  {
    path: '/terms-conditions',
    element: <TermsConditions />,
    handle: { breadcrumb: 'Terms & Conditions', sidebarPlacement: NAV_PLACEMENTS.FOOTER, nav: nav(NAV_PLACEMENTS.FOOTER, 'TERMS & CONDITIONS', { footerLabel: 'Terms & Conditions', order: 20 }) },
  },
];

export default vivosolRouteConfig;
